import React from 'react';
import swal from 'sweetalert';

import Button from '../../Button';
import Toggle from '../../Toggle';

import { updateDepartment, sortArrayOfObjects, isEmpty } from '../../../js/Helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faUnlock, faUserCircle } from '@fortawesome/free-solid-svg-icons';

export default function ManagePermissions({ isLoading, subscreen, subscreenRestricted, subscreenLocked, departments, setDepartments, userInfo, schoolInfo, rolePages, handleMoveToNextScreen }) {
	const current_user_is_admin = (userInfo.role_id === '1'|| userInfo.role_id === '2' || userInfo.role_id === '5');
	const role_page_subscreens = rolePages?.manage_departments?.subscreens || [];
	const subscreen_index = role_page_subscreens.findIndex(temp_subscreen => temp_subscreen.subscreen === subscreen);
	const subscreen_is_complete = (subscreen_index !== -1) ? role_page_subscreens[subscreen_index].complete : false;
	const display_departments = isLoading ? [] : sortArrayOfObjects(departments, 'department', 'text', 'asc');

	const saveDepartments = () => {
		sessionStorage.setItem('departments', JSON.stringify(departments));
		setDepartments([...departments]);
	};

	const showSaveError = (message = 'Unable to update this supervisor permission. Please try again.') => {
		swal({
			title: 'Permission Not Saved',
			text: message,
			icon: 'error',
			buttons: {
				cancel: {
					text: 'Okay',
					value: false,
					visible: true,
					className: 'gray-btn'
				},
			}
		});
	};

	const toggleCrossDepartmentEditing = (supervisor_user_id, next_toggle_state = null) => {
		const matching_supervisors = [];

		departments.forEach(department_info => {
			(department_info.supervisors || []).forEach(supervisor_info => {
				if(`${supervisor_info.user_id}` === `${supervisor_user_id}`) matching_supervisors.push(supervisor_info);
			});
		});

		if(isEmpty(matching_supervisors) || matching_supervisors.some(supervisor_info => supervisor_info.cross_department_editing_saving)) return;

		const previous_value = matching_supervisors.some(supervisor_info => supervisor_info.allow_cross_department_editing === '1') ? '1' : '0';
		const next_value = next_toggle_state === null ? (previous_value === '1' ? '0' : '1') : (next_toggle_state ? '1' : '0');
		const revertToggle = () => {
			matching_supervisors.forEach(supervisor_info => {
				supervisor_info.allow_cross_department_editing = previous_value;
			});
		};

		matching_supervisors.forEach(supervisor_info => {
			supervisor_info.allow_cross_department_editing = next_value;
			supervisor_info.cross_department_editing_saving = true;
		});
		saveDepartments();

		updateDepartment({update_type:'allow_cross_department_editing', school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, user_id:supervisor_user_id, allow_cross_department_editing:next_value}).then(data => {
			if(!data || data.status === 'error')
			{
				revertToggle();
				if(data && data.error) console.error('Cross-department permission save failed:', data.error);
				const error_detail = data && data.error ? `\n\n${data.error}` : '';
				showSaveError(data && data.message ? `${data.message}${error_detail}` : undefined);
			}
		}).catch(error => {
			revertToggle();
			const response_data = error && error.response && error.response.data ? error.response.data : null;
			const response_message = response_data && response_data.message ? response_data.message : null;
			const response_error = response_data && response_data.error ? response_data.error : null;
			if(response_error || error) console.error('Cross-department permission save failed:', response_error || error);
			showSaveError(response_message ? `${response_message}${response_error ? `\n\n${response_error}` : ''}` : undefined);
		}).finally(() => {
			matching_supervisors.forEach(supervisor_info => {
				delete supervisor_info.cross_department_editing_saving;
			});
			saveDepartments();
		});
	};

	const toggleAllowManualPlacements = (department_id, supervisor_id, toggle_state) => {
		const department_index = departments.findIndex(department => `${department.department_id}` === `${department_id}`);
		if(department_index === -1) return;

		const supervisor_index = (departments[department_index].supervisors || []).findIndex(supervisor => `${supervisor.user_id}` === `${supervisor_id}`);
		if(supervisor_index === -1 || departments[department_index].supervisors[supervisor_index].manual_placements_saving) return;

		const previous_value = departments[department_index].supervisors[supervisor_index].allow_manual_placements;
		const allow_manual_placements = toggle_state ? '1' : '0';
		departments[department_index].supervisors[supervisor_index].allow_manual_placements = allow_manual_placements;
		departments[department_index].supervisors[supervisor_index].manual_placements_saving = true;
		saveDepartments();

		updateDepartment({update_type:'allow_manual_placements', school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, department_id:department_id, user_id:supervisor_id}).catch(error => {
			departments[department_index].supervisors[supervisor_index].allow_manual_placements = previous_value;
			console.error('Manual placement permission save failed:', error);
			showSaveError();
		}).finally(() => {
			delete departments[department_index].supervisors[supervisor_index].manual_placements_saving;
			saveDepartments();
		});
	};

	return (
		<>
		<h1 className='progress-main-header'>Manage Permissions</h1>
		<p className='progress-subheader'>Control which supervisors can edit across department boundaries and which supervisors can manually place sections before Edario runs automatic placement.</p>
		{isLoading ?
			(
				<div className='center-text'>
					<img src={require('../../../images/balls.gif')} alt='Loading...' style={{height:'40px'}}/>
				</div>
			): (subscreenRestricted) ?
			(
				<div className='general-screen-message'>
					<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
					<h1>No Access</h1>
					<div>You do not have access to this screen</div>
				</div>
			): (subscreenLocked) ?
			(
				<div className='general-screen-message'>
					<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
					<h1>Temporarily Locked</h1>
					<div>This screen is locked until all other prior steps are completed</div>
				</div>
			): current_user_is_admin ? (
				<>
				<div className='section-placement-container manage-permissions-container'>
					<div className='manage-permissions-card-list'>
						{display_departments.map((department_info, index) => {
							const department_id = department_info.department_id;
							const department_supervisors = department_info.supervisors || [];

							return (
								<div className='manage-permissions-card' key={index} data-id={department_id}>
									<div className='manage-permissions-card-header'>
										<div>
											<div className='manage-permissions-department-name'>{department_info.department}</div>
											<div className='manage-permissions-department-subtext'>{department_supervisors.length === 1 ? '1 supervisor' : `${department_supervisors.length} supervisors`}</div>
										</div>
									</div>
									<div className='manage-permissions-supervisor-list'>
										{isEmpty(department_supervisors) &&
											<div className='manage-permissions-empty-supervisor'>
												<div className='department-supervisor-empty-container'>
													<FontAwesomeIcon className='department-supervisor-empty-icon' icon={faUserCircle}/>
													<div className='department-supervisor-empty-text'>No Department Supervisor</div>
												</div>
											</div>
										}
										{department_supervisors.map((supervisor_info, supervisor_index) => {
											const supervisor_id = supervisor_info.user_id;
											const cross_department_editing_allowed = supervisor_info.allow_cross_department_editing === '1';
											const manual_placements_allowed = supervisor_info.allow_manual_placements === '1';

											return (
												<div className='manage-permissions-supervisor-section' key={supervisor_index}>
													<div className='manage-permissions-supervisor-heading'>
														{supervisor_info.img_url &&
															<img className='department-supervisor-img' src={require(`../../../images/users/${supervisor_info.img_url}`)} alt='supervisor' />
														}
														{!supervisor_info.img_url &&
															<FontAwesomeIcon className='department-supervisor-user-icon' icon={faUserCircle}/>
														}
														<div className='department-supervisor-name'>{supervisor_info.last_name}, {supervisor_info.first_name}</div>
													</div>
													<div className='manage-permissions-permission-list'>
														<div className='manage-permissions-permission-row'>
															<div>
																<div className='manage-permissions-permission-title'>Cross-Department Editing</div>
																<div className='manage-permissions-permission-copy'>Can use existing teachers, courses, and rooms from other departments while editing sections.</div>
															</div>
															<div className='manage-permissions-toggle-container'>
																<Toggle
																	key={`${supervisor_id}-${cross_department_editing_allowed}-${!!supervisor_info.cross_department_editing_saving}`}
																	toggleOn={cross_department_editing_allowed}
																	disabled={!!supervisor_info.cross_department_editing_saving}
																	handleChange={(new_state) => toggleCrossDepartmentEditing(supervisor_id, new_state)}
																/>
																{supervisor_info.cross_department_editing_saving &&
																	<img src={require('../../../images/balls.gif')} alt='saving...' />
																}
															</div>
														</div>
														<div className='manage-permissions-permission-row'>
															<div>
																<div className='manage-permissions-permission-title'>Manual Placements</div>
																<div className='manage-permissions-permission-copy'>Can manually place and adjust sections before automatic placement runs.</div>
															</div>
															<div className='manage-permissions-toggle-container'>
																<Toggle
																	key={`${department_id}-${supervisor_id}-${manual_placements_allowed}-${!!supervisor_info.manual_placements_saving}`}
																	toggleOn={manual_placements_allowed}
																	disabled={!!supervisor_info.manual_placements_saving}
																	handleChange={(new_state) => toggleAllowManualPlacements(department_id, supervisor_id, new_state)}
																/>
																{supervisor_info.manual_placements_saving &&
																	<img src={require('../../../images/balls.gif')} alt='saving...' />
																}
															</div>
														</div>
													</div>
												</div>
											)
										})}
									</div>
								</div>
							)
						})}
					</div>
				</div>
				<div className="clear"></div>
				<div className='fixed-bottom-save-container'>
					<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Continue to Data Cleaning' onClickFunction={handleMoveToNextScreen} />
				</div>
				</>
			) : null
		}
		{userInfo && !current_user_is_admin && !subscreenRestricted && !subscreenLocked && !subscreen_is_complete &&
			<div className='locked-screen-notification-container'>
				<FontAwesomeIcon className='dark-blue-text' icon={faLock} style={{fontSize:'50px'}}/>
				<h1>Locked</h1>
				<p>This step is only accessible to the master scheduler. You should be notified when future steps are unlocked!</p>
			</div>
		}
		{userInfo && !current_user_is_admin && !subscreenRestricted && !subscreenLocked && subscreen_is_complete &&
			<div className='locked-screen-notification-container'>
				<FontAwesomeIcon className='green-text' icon={faUnlock} style={{fontSize:'50px'}}/>
				<h1>Step Unlocked!</h1>
				<p>The scheduling admin has finished managing department permissions, and you can now move on to the next step!</p>
			</div>
		}
		</>
	);
}
